import { ErrorScreen } from "@/components/ErrorScreen";
import { CodeGenLayout } from "@/components/layout/codegen-layout";
import { useInputFocus } from "@/hooks";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useEventsWebSocket } from "@/lib/hooks/useEventsWebSocket";
import { listProjectsQueryOptions } from "@/lib/hooks/useProjects";
import {
  useAttachmentStore,
  useInputStore,
  useMessageStore,
  usePanelStore,
} from "@/state/stores";
import { captureException } from "@sentry/react";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createFileRoute("/(projects)/projects")({
  component: Layout,
  loader: async ({ context: { queryClient } }) => {
    const result = await queryClient.ensureQueryData(
      listProjectsQueryOptions(),
    );
    return {
      projects: result.data.projects,
    };
  },
  errorComponent: ({ error }) => {
    useEffect(() => {
      captureException(error);
    }, [error]);

    return (
      <ErrorScreen
        error={error}
        title="Error loading Projects"
        message="An unknown error occurred, which is our least favorite kind of error."
      />
    );
  },
});

function Layout() {
  useEventsWebSocket();

  const setMessageLoading = useMessageStore((state) => state.setLoading);
  const clearProjectData = useMessageStore((state) => state.clearProjectData);
  const clearInput = useInputStore((state) => state.clearInput);
  const clearAttachments = useAttachmentStore((state) => state.clearAll);
  const { openPanel, closePanel } = usePanelStore();

  const projectId = useActiveProjectId();
  const focusInput = useInputFocus();

  useEffect(() => {
    clearAttachments();

    // Focus the input if we're on a new project page. Otherwise, the input focus
    // determination will be handled by Chat.tsx (depending on loading messages,
    // thinking, etc.)
    if (!projectId) {
      focusInput();
      closePanel();
      clearProjectData();
      return;
    }

    openPanel("suggestions");
  }, [
    projectId,
    clearAttachments,
    closePanel,
    focusInput,
    openPanel,
    clearProjectData,
  ]);

  useEffect(() => {
    setMessageLoading(false);
    clearProjectData();
    clearInput();
  }, [clearProjectData, setMessageLoading, clearInput]);

  return (
    <CodeGenLayout>
      <Outlet />
    </CodeGenLayout>
  );
}
